import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { DesktopSidebarComponent } from "../../../../shared/components/desktop-sidebar/desktop-sidebar.component";
import { DesktopTopbarComponent } from "../../../../shared/components/desktop-topbar/desktop-topbar.component";
import { MobileTopbarComponent } from "../../../../shared/components/mobile-topbar/mobile-topbar.component";
import { TabButtonComponent } from "../../../../shared/components/tab-button/tab-button.component";
import { CustomDropdownComponent } from "../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { InvoiceStatusComponent } from "../../components/invoice-status/invoice-status.component";
import { CommonModule } from '@angular/common';
import { ExpandedGasInvoiceComponent } from "../../components/expanded-gas-invoice/expanded-gas-invoice.component";
import { InvoiceUploadDialogComponent } from "../../components/invoice-upload-dialog/invoice-upload-dialog.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { GasInvoice } from '../../../../core/interfaces/invoice-interfaces';
import { InvoiceHelperService } from '../../../../core/services/invoice-helper.service';
import { IsoToFormattedPeriodPipe } from '../../../../core/pipes/iso-to-formatted-period.pipe';
import { combineLatest } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaceFileDialogComponent } from "../../../file-upload/components/dialogs/replace-file-dialog/replace-file-dialog.component";
import { FileUploadEnergyType, FileUploadFileType } from '../../../../core/interfaces/file-upload-interfaces';
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { InvoiceFileDisputeDialogComponent } from "../../components/invoice-file-dispute-dialog/invoice-file-dispute-dialog.component";
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-invoice-gas-page',
  standalone: true,
  imports: [DesktopSidebarComponent, DesktopTopbarComponent, MobileTopbarComponent, TabButtonComponent, CustomDropdownComponent, CustomButtonComponent, InvoiceStatusComponent, CommonModule, ExpandedGasInvoiceComponent, InvoiceUploadDialogComponent, TranslocoPipe, IsoToFormattedPeriodPipe, FormsModule, ToastComponent, ReplaceFileDialogComponent, CustomFooterComponent, InvoiceFileDisputeDialogComponent],
  templateUrl: './invoice-gas-page.component.html',
  styleUrl: './invoice-gas-page.component.css',
  animations: [
    trigger('expandRow', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('500ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class InvoiceGasPageComponent {
  selectedSidebarItemId: number = 1;

  // Dropdown Menu
  filterDropdownOptions: string[] = [
    'Filter',
    'By Date (ASC)',
    'By Date (DESC)',
    'By Invoice Number (ASC)',
    'By Invoice Number (DESC)',
    'By Consumption (ASC)',
    'By Consumption (DESC)',
    'By Costs (ASC)',
    'By Costs (DESC)',
    'By Status (Check)',
    'By Status (Review Needed)'
  ];
  selectedFilterDropdown: number | undefined;

  // Search filter
  searchTerm: string = '';

  // Invoices Data
  invoices: GasInvoice[] = [];
  loadedInvoices: GasInvoice[] = [];

  lastLoadedPageNumber: number = 0;
  loadMoreHidden = false;

  // Expanded Row Id
  expandedRow: number | null = null;

  //Upload Dialog
  @ViewChild(InvoiceUploadDialogComponent) invoiceUploadDialog!: InvoiceUploadDialogComponent;
  energyType: FileUploadEnergyType = FileUploadEnergyType.GAS;
  fileType: FileUploadFileType = FileUploadFileType.INVOICE;

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Replace File dialog
  @ViewChild(ReplaceFileDialogComponent) replaceFileDialog!: ReplaceFileDialogComponent;

  // File dispute dialog
  @ViewChild(InvoiceFileDisputeDialogComponent) disputeDialog!: InvoiceFileDisputeDialogComponent;

  constructor(private translocoService: TranslocoService, private invoiceHelper: InvoiceHelperService, private dateConversion: DateConversionService, private invoiceService: InvoiceService, private apiError: ApiErrorService, private cd: ChangeDetectorRef) { }

  ngOnInit(): void {
    const filterKeys = [
      'invoice.filters.default',
      'invoice.filters.date_asc',
      'invoice.filters.date_desc',
      'invoice.filters.invoice_number_asc',
      'invoice.filters.invoice_number_desc',
      'invoice.filters.consumption_asc',
      'invoice.filters.consumption_desc',
      'invoice.filters.costs_asc',
      'invoice.filters.costs_desc',
      'invoice.filters.status_check',
      'invoice.filters.status_review_needed'
    ];

    combineLatest(filterKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.filterDropdownOptions = translations;
    });

    this.selectedFilterDropdown = 0;

    // Load Invoices First Page
    this.lastLoadedPageNumber = 0;
    this.loadGasInvoicesNewPage();
    this.resetInvoicesFilter();

    this.cd.detectChanges();
  }

  toggleDetails(index: number): void {
    this.expandedRow = this.expandedRow === index ? null : index;
  }

  openInvoiceUploadDialog(): void {
    this.invoiceUploadDialog.openModal();
  }

  getInvoiceStatus(invoice: GasInvoice): boolean {
    return this.invoiceHelper.getGasInvoiceStatus(invoice);
  }

  loadGasInvoicesNewPage() {
    this.invoiceService.getGasInvoice((this.lastLoadedPageNumber + 1)).subscribe({
      next: (response) => {
        if (response) {
          if (response.data.length === 0 || this.loadedInvoices.length + response.data.length >= response.totalCount) {
            this.loadMoreHidden = true;
          }

          this.loadedInvoices = [...this.loadedInvoices, ...response.data];

          // Applay filter
          this.resetInvoicesFilter();
          this.applySelectedFilter();
          this.applySearchFilter();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
        this.lastLoadedPageNumber++;
      }
    });
  }

  resetInvoicesFilter() {
    this.invoices = [];
    this.invoices = this.loadedInvoices;
  }

  applySelectedFilter() {
    if (this.selectedFilterDropdown === undefined) {
      return;
    }
    this.expandedRow = null;

    const optionIndex = this.selectedFilterDropdown;

    if (optionIndex === 0) {
      this.resetInvoicesFilter();
    }
    else if (optionIndex === 1) {
      this.invoices.sort((a, b) => new Date(a.for_period ?? '').getTime() - new Date(b.for_period ?? '').getTime());
    } else if (optionIndex === 2) {
      this.invoices.sort((a, b) => new Date(b.for_period ?? '').getTime() - new Date(a.for_period ?? '').getTime());
    } else if (optionIndex === 3) {
      this.invoices.sort((a, b) => (a.invoice_number ?? '').localeCompare((b.invoice_number ?? '')));
    } else if (optionIndex === 4) {
      this.invoices.sort((a, b) => (b.invoice_number ?? '').localeCompare((a.invoice_number ?? '')));
    } else if (optionIndex === 5) {
      this.invoices.sort((a, b) => (a.total_consumption ?? 0) - (b.total_consumption ?? 0));
    } else if (optionIndex === 6) {
      this.invoices.sort((a, b) => (b.total_consumption ?? 0) - (a.total_consumption ?? 0));
    } else if (optionIndex === 7) {
      this.invoices.sort((a, b) => (a.invoice_amount_net_actual ?? 0) - (b.invoice_amount_net_actual ?? 0));
    } else if (optionIndex === 8) {
      this.invoices.sort((a, b) => (b.invoice_amount_net_actual ?? 0) - (a.invoice_amount_net_actual ?? 0));
    } else if (optionIndex === 9) {
      this.resetInvoicesFilter();
      this.invoices = this.invoices.filter(invoice => this.getInvoiceStatus(invoice) === true);
    } else if (optionIndex === 10) {
      this.resetInvoicesFilter();
      this.invoices = this.invoices.filter(invoice => this.getInvoiceStatus(invoice) === false);
    }
  }

  applySearchFilter() {
    this.expandedRow = null;
    this.resetInvoicesFilter();
    const term = this.searchTerm.trim().toLowerCase();

    this.invoices = this.invoices.filter(invoice => {
      return (
        invoice.invoice_number!.toLowerCase().includes(term) ||
        this.dateConversion.isoToFormattedPeriod(invoice.for_period!).toLowerCase().includes(term) ||
        invoice.total_consumption!.toString().includes(term) ||
        invoice.invoice_amount_net_actual!.toString().includes(term)
      );
    });
  }

  exportCsv() {
    this.invoiceHelper.exportGasInvoicesToCSV(this.invoices);
  }

  reloadInvoices = () => {
    this.loadedInvoices = [];
    this.lastLoadedPageNumber = 0;
    this.loadGasInvoicesNewPage();
    this.resetInvoicesFilter();
  };
}
